import { Button, Stack, useTheme } from "@mui/material";
import { Plus } from "lucide-react";
import type { FC } from "react";
import { DashboardCategorySection } from "web_myplann/Dashboard/Components/DashboardCategorySection";
import { DashboardDetailSkeleton } from "web_myplann/Dashboard/Components/DashboardDetailSkeleton";
import { useCanAddDashboardItem } from "web_myplann/Dashboard/Hooks/useCanAddDashboardItem";
import { useDashboardTotalSections } from "web_myplann/Dashboard/Hooks/useDashboardTotalSections";
import { useNavigateDashboardAddItem } from "web_myplann/Dashboard/Hooks/useNavigateDashboardAddItem";
import type { DashboardType } from "web_myplann/Dashboard/Types/DashboardType";
import { PortfolioReportingOverviewTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type Props = {
	dashboards: DashboardType[];
};

export const DashboardTotalContent: FC<Props> = ({ dashboards }) => {
	const { t } = useResource();
	const { spacingTokens, iconSize } = useTheme();
	const canAddItem = useCanAddDashboardItem();
	const { sections, isLoading } = useDashboardTotalSections(dashboards);
	const navigateToAddItem = useNavigateDashboardAddItem(
		PortfolioReportingOverviewTypeEnum.Total,
	);

	if (isLoading) {
		return (
			<DashboardDetailSkeleton
				cards={sections.length}
				rowsPerCard={2}
				withHeader
			/>
		);
	}

	return (
		<Stack
			flex={1}
			pt={spacingTokens.sm}
			pb={spacingTokens.xl}
			gap={spacingTokens.sm}
		>
			{sections.map((section) => (
				<DashboardCategorySection
					key={section.overviewType}
					section={section}
				/>
			))}
			{canAddItem && (
				<Stack bgcolor="background.paper">
					<Button
						variant="listCta"
						startIcon={<Plus size={iconSize.sm} />}
						onClick={navigateToAddItem}
					>
						{t(
							Resources.Dashboard.Detail.Add[
								PortfolioReportingOverviewTypeEnum.Total
							],
						)}
					</Button>
				</Stack>
			)}
		</Stack>
	);
};
